import React, { useState, useEffect, useCallback } from 'react';
import { ColumnsType } from 'antd/es/table';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { CopyOutlined, DeleteOutlined, GiftOutlined, PlusOutlined, ReloadOutlined, TeamOutlined } from '@ant-design/icons';
import moment from 'moment';
import { BaseTable } from '@app/components/common/BaseTable/BaseTable';
import { notificationController } from '@app/controllers/notificationController';
import { useAppDispatch } from '@app/hooks/reduxHooks';
import {
  getFirstRechargeConfigAction,
  getGenericCouponsAction,
  createGenericCouponAction,
  toggleGenericCouponAction,
  getGenericRedemptionsAction,
  deleteCouponAction,
} from '@app/store/slices/userSlice';

const { Text } = Typography;

/**
 * "Generic Coupons" tab.
 * One code that works for EVERY user, but only once per user and only on their first
 * wallet recharge (the same rule as single-use coupons). No usage limit.
 * The offer amount always comes from the shared First Recharge Offer Settings.
 */
export const GenericCoupons: React.FC = () => {
  const dispatch = useAppDispatch();

  const [coupons, setCoupons] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({ totalGenericCoupons: 0, activeGenericCoupons: 0, totalRedemptions: 0 });
  const [config, setConfig] = useState<any>(null);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();

  // Redemptions modal
  const [redeemOpen, setRedeemOpen] = useState(false);
  const [redeemLoading, setRedeemLoading] = useState(false);
  const [redeemCoupon, setRedeemCoupon] = useState<any>(null);
  const [redemptions, setRedemptions] = useState<any[]>([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await dispatch(getGenericCouponsAction()).unwrap();
      if (res.success) {
        setCoupons(res.data || []);
        if (res.stats) setStats(res.stats);
      }
    } catch (err: any) {
      notificationController.error({ message: err?.message || err || 'Failed to load generic coupons' });
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  const fetchConfig = useCallback(async () => {
    try {
      const res: any = await dispatch(getFirstRechargeConfigAction()).unwrap();
      if (res?.data) setConfig(res.data);
    } catch (err) {
      // non-critical: only used for the info banner
    }
  }, [dispatch]);

  useEffect(() => {
    fetchData();
    fetchConfig();
  }, [fetchData, fetchConfig]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success(`Copied "${text}" to clipboard!`);
  };

  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      const res: any = await dispatch(createGenericCouponAction({ code: values.code })).unwrap();
      if (res.success) {
        notificationController.success({ message: res.message || 'Generic coupon created.' });
        setCreateOpen(false);
        createForm.resetFields();
        fetchData();
      }
    } catch (err: any) {
      if (err?.errorFields) return;
      notificationController.error({ message: err?.message || err || 'Failed to create coupon.' });
    } finally {
      setCreateLoading(false);
    }
  };

  const handleToggle = async (record: any, checked: boolean) => {
    try {
      await dispatch(toggleGenericCouponAction({ id: record._id, is_active: checked })).unwrap();
      notificationController.success({ message: `Coupon ${record.code} is now ${checked ? 'active' : 'inactive'}.` });
      fetchData();
    } catch (err: any) {
      notificationController.error({ message: err?.message || err || 'Failed to update coupon.' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await dispatch(deleteCouponAction(id)).unwrap();
      notificationController.success({ message: 'Coupon deleted successfully.' });
      fetchData();
    } catch (err: any) {
      notificationController.error({ message: err?.message || err || 'Failed to delete coupon.' });
    }
  };

  const openRedemptions = async (record: any) => {
    setRedeemCoupon(record);
    setRedemptions([]);
    setRedeemOpen(true);
    setRedeemLoading(true);
    try {
      const res: any = await dispatch(getGenericRedemptionsAction(record._id)).unwrap();
      if (res.success) setRedemptions(res.data || []);
    } catch (err: any) {
      notificationController.error({ message: err?.message || err || 'Failed to load redemptions.' });
    } finally {
      setRedeemLoading(false);
    }
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Coupon Code',
      dataIndex: 'code',
      key: 'code',
      render: (code: string) => (
        <Space>
          <Text strong style={{ letterSpacing: '1px', fontFamily: 'monospace', fontSize: '14px' }}>
            {code}
          </Text>
          <Button size="small" type="text" icon={<CopyOutlined />} onClick={() => copyToClipboard(code)} title="Copy code" />
        </Space>
      ),
    },
    {
      title: 'Type',
      key: 'type',
      render: () => <Tag color="purple">Generic (all users)</Tag>,
    },
    {
      title: 'Status',
      key: 'is_active',
      render: (_: any, record: any) => (
        <Space>
          <Switch
            checked={record.is_active !== false}
            checkedChildren="Active"
            unCheckedChildren="Off"
            onChange={(checked) => handleToggle(record, checked)}
          />
        </Space>
      ),
    },
    {
      title: 'Redeemed By',
      dataIndex: 'redemption_count',
      key: 'redemption_count',
      render: (count: number, record: any) => (
        <Button type="link" icon={<TeamOutlined />} onClick={() => openRedemptions(record)} style={{ padding: 0 }}>
          {count || 0} user{(count || 0) === 1 ? '' : 's'}
        </Button>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => (createdAt ? moment(createdAt).format('DD MMM YYYY, hh:mm A') : '-'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: any) =>
        (record.redemption_count || 0) === 0 ? (
          <Popconfirm
            title="Delete this coupon?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} size="small" />
          </Popconfirm>
        ) : (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Switch off to stop
          </Text>
        ),
    },
  ];

  const redemptionColumns: ColumnsType<any> = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone' },
    { title: 'Wallet Credit', dataIndex: 'credited', key: 'credited', render: (v: number) => `₹${v}` },
    { title: 'Discount', dataIndex: 'discount', key: 'discount', render: (v: number) => `₹${v}` },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (d: string) => (d ? moment(d).format('DD MMM YYYY, hh:mm A') : '-'),
    },
  ];

  const payable = config ? Math.max(0, (config.recharge_amount || 0) - (config.discount_amount || 0)) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Alert
        type="info"
        showIcon
        message="One code for everyone"
        description={
          <div>
            A generic coupon works for <b>every user</b>, but only <b>once per user</b> and only on their{' '}
            <b>first wallet recharge</b>. There is no usage limit. Switch it off any time to stop it.
            {config ? (
              <div style={{ marginTop: '6px' }}>
                Current offer (set in the <b>Single-Use Coupons</b> tab): customer pays <b>₹{payable}</b> and gets{' '}
                <b>₹{config.recharge_amount}</b> wallet credit.
                {!config.enabled && (
                  <Text type="danger"> The offer is currently DISABLED, so no coupon will work.</Text>
                )}
              </div>
            ) : null}
          </div>
        }
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Generic Coupons"
              value={stats.totalGenericCoupons}
              prefix={<GiftOutlined style={{ color: '#722ed1' }} />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Active" value={stats.activeGenericCoupons} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic title="Total Redemptions" value={stats.totalRedemptions} valueStyle={{ color: '#fa8c16' }} />
          </Card>
        </Col>
      </Row>

      <Card
        title="Generic Coupons"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => fetchData()}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                createForm.resetFields();
                setCreateOpen(true);
              }}
            >
              Create Generic Coupon
            </Button>
          </Space>
        }
      >
        <BaseTable loading={loading} columns={columns} dataSource={coupons} rowKey="_id" pagination={false} />
      </Card>

      <Modal
        title="Create Generic Coupon"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        confirmLoading={createLoading}
        okText="Create"
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="code"
            label="Coupon Code"
            normalize={(v) => (v || '').toUpperCase().replace(/[^A-Z0-9]/g, '')}
            rules={[
              { required: true, message: 'Please enter a code' },
              { min: 4, message: 'At least 4 characters' },
              { max: 20, message: 'At most 20 characters' },
            ]}
            extra="Letters and numbers only, e.g. VIJAY100. This is the code your team shows to customers."
          >
            <Input placeholder="e.g. VIJAY100" maxLength={20} style={{ letterSpacing: '1px', fontWeight: 'bold' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Redemptions${redeemCoupon ? ` — ${redeemCoupon.code}` : ''}`}
        open={redeemOpen}
        onCancel={() => setRedeemOpen(false)}
        footer={<Button onClick={() => setRedeemOpen(false)}>Close</Button>}
        width={720}
      >
        <Table
          loading={redeemLoading}
          columns={redemptionColumns}
          dataSource={redemptions}
          rowKey="_id"
          size="small"
          pagination={{ pageSize: 10 }}
        />
      </Modal>
    </div>
  );
};
